import React, { useState } from 'react';
import { ArrowLeftIcon, CheckIcon, XIcon } from 'lucide-react';
// Transaction types grouped by their nature
const transactionTypeGroups = [{
  name: 'P2P Transactions',
  transactions: [{
    id: 'p2p_transfer',
    name: 'P2P Transfer'
  }, {
    id: 'account_identification',
    name: 'Account Identification'
  }, {
    id: 'alias_management',
    name: 'Alias Management'
  }]
}, {
  name: 'P2M Issuer Transactions',
  transactions: [{
    id: 'p2m_sqrc',
    name: 'P2M Payments - SQRC'
  }, {
    id: 'p2m_dqrc',
    name: 'P2M Payments - DQRC'
  }, {
    id: 'p2m_rtp_later',
    name: 'P2M Payments - RTP Later'
  }, {
    id: 'p2m_rtp_now',
    name: 'P2M Payments - RTP Now'
  }, {
    id: 'pisp_issuer',
    name: 'PISP - Issuer'
  }]
}, {
  name: 'P2M Acquirer Transactions',
  transactions: [{
    id: 'p2m_sqrc_acquirer',
    name: 'P2M Payments - SQRC - Acquirer'
  }, {
    id: 'p2m_dqrc_acquirer',
    name: 'P2M Payments - DQRC - Acquirer'
  }, {
    id: 'p2m_rtp_later_acquirer',
    name: 'P2M Payments - RTP Later - Acquirer'
  }, {
    id: 'p2m_rtp_now_acquirer',
    name: 'P2M Payments - RTP Now - Acquirer'
  }, {
    id: 'pisp_acquirer',
    name: 'PISP - Acquirer'
  }]
}, {
  name: 'Bulk Transactions',
  transactions: [{
    id: 'bulk_receiving',
    name: 'Bulk Receiving'
  }, {
    id: 'bulk_sending',
    name: 'Bulk Sending'
  }]
}];
interface AddParticipantProps {
  onNavigate?: (path: string) => void;
}
export function AddParticipant({
  onNavigate
}: AddParticipantProps = {}) {
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    bic: '',
    status: 'Active',
    type: 'Bank',
    activationDate: new Date().toISOString().split('T')[0],
    enabledTransactions: [] as string[]
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleTransactionToggle = (transactionId: string) => {
    setFormData(prevData => {
      if (prevData.enabledTransactions.includes(transactionId)) {
        return {
          ...prevData,
          enabledTransactions: prevData.enabledTransactions.filter(id => id !== transactionId)
        };
      } else {
        return {
          ...prevData,
          enabledTransactions: [...prevData.enabledTransactions, transactionId]
        };
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    // Here you would normally submit to your API
    console.log('Participant data to be submitted:', formData);
    // Reset form after submission
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        name: '',
        shortName: '',
        bic: '',
        status: 'Active',
        type: 'Bank',
        activationDate: new Date().toISOString().split('T')[0],
        enabledTransactions: []
      });
    }, 3000);
  };
  // Navigate back to the participants list
  const handleBack = () => {
    if (onNavigate) {
      onNavigate('participants/list');
    } else {
      window.location.href = '#/participants/list';
    }
  };
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-708">
      <div className="flex items-center mb-6" data-id="element-709">
        <button className="mr-4 p-2 rounded-full hover:bg-gray-100" onClick={handleBack} data-id="element-710">
          <ArrowLeftIcon size={20} data-id="element-711" />
        </button>
        <h1 className="text-2xl font-semibold text-[#1A1F36]" data-id="element-712">
          Add New Participant
        </h1>
      </div>
      {formSubmitted ? <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6" data-id="element-713">
          <div className="flex" data-id="element-714">
            <div className="flex-shrink-0" data-id="element-715">
              <CheckIcon className="h-5 w-5 text-green-400" data-id="element-716" />
            </div>
            <div className="ml-3" data-id="element-717">
              <p className="text-sm font-medium text-green-800" data-id="element-718">
                Participant has been successfully added and is pending approval.
              </p>
            </div>
          </div>
        </div> : <form onSubmit={handleSubmit} data-id="element-719">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-720">
            <div data-id="element-721">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-722">
                Participant Name*
              </label>
              <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter full participant name" data-id="element-723" />
            </div>
            <div data-id="element-724">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-725">
                Short Name*
              </label>
              <input type="text" name="shortName" required value={formData.shortName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter short name (abbreviation)" maxLength={10} data-id="element-726" />
            </div>
            <div data-id="element-727">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-728">
                BIC*
              </label>
              <input type="text" name="bic" required value={formData.bic} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter BIC" maxLength={11} data-id="element-729" />
            </div>
            <div data-id="element-730">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-731">
                Status*
              </label>
              <select name="status" required value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-732">
                <option value="Active" data-id="element-733">Active</option>
                <option value="In-Active" data-id="element-734">In-Active</option>
                <option value="Closed" data-id="element-735">Closed</option>
              </select>
            </div>
            <div data-id="element-736">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-737">
                Participant Type*
              </label>
              <select name="type" required value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-738">
                <option value="Bank" data-id="element-739">Bank</option>
                <option value="MSP" data-id="element-740">MSP</option>
                <option value="Aggregator" data-id="element-741">Aggregator</option>
              </select>
            </div>
            <div data-id="element-742">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-743">
                Activation Date*
              </label>
              <input type="date" name="activationDate" required value={formData.activationDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-744" />
            </div>
          </div>
          <div className="mb-8" data-id="element-745">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-746">
              Allowed Transactions
            </h2>
            {transactionTypeGroups.map(group => <div key={group.name} className="mb-6" data-id="element-747">
                <h3 className="text-md font-medium text-gray-800 mb-3 border-b pb-2" data-id="element-748">
                  {group.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-749">
                  {group.transactions.map(transaction => <div key={transaction.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50" data-id="element-750">
                      <input type="checkbox" id={transaction.id} checked={formData.enabledTransactions.includes(transaction.id)} onChange={() => handleTransactionToggle(transaction.id)} className="h-4 w-4 text-[#F58220] focus:ring-[#F58220] border-gray-300 rounded" data-id="element-751" />
                      <label htmlFor={transaction.id} className="text-sm text-gray-700 cursor-pointer" data-id="element-752">
                        {transaction.name}
                      </label>
                    </div>)}
                </div>
              </div>)}
          </div>
          <div className="border-t border-gray-200 pt-6 flex justify-end space-x-4" data-id="element-753">
            <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F58220]" onClick={handleBack} data-id="element-754">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F58220] hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F58220]" data-id="element-755">
              Submit for Approval
            </button>
          </div>
        </form>}
      <div className="mt-6 text-sm text-gray-500" data-id="element-756">
        <p data-id="element-757">* Required fields</p>
        <p data-id="element-758">
          Note: All participant additions require approval from an authorized
          checker.
        </p>
      </div>
    </div>;
}